import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import * as SkeletonUtils from 'three/examples/jsm/utils/SkeletonUtils';
import * as CANNON from 'cannon-es';
import {
    CHARACTER_GLB_URL,
    CHARACTER_ANIM_FILES,
    CHARACTER_SPAWN_COUNT,
    SPAWN_HEIGHT,
    GROUND_Y,
    SHOW_RAGDOLL_DEBUG,
    RAGDOLL_JOINTS,
    DEFAULT_DOJO_CAMERA,
} from './config';
import { remapMixamoClip, findHipsBone } from './characterUtils';
import { createCharacterPhysicsBody } from './physicsSetup';

const buildSpawnPositions = () => {
    const _spawnFwd = new THREE.Vector3(
        DEFAULT_DOJO_CAMERA.target.x - DEFAULT_DOJO_CAMERA.position.x,
        0,
        DEFAULT_DOJO_CAMERA.target.z - DEFAULT_DOJO_CAMERA.position.z
    ).normalize();
    const _spawnRight = new THREE.Vector3().crossVectors(_spawnFwd, new THREE.Vector3(0, 1, 0)).normalize();
    const _spawnBase  = new THREE.Vector3(
        DEFAULT_DOJO_CAMERA.position.x,
        GROUND_Y,
        DEFAULT_DOJO_CAMERA.position.z
    );
    return Array.from({ length: CHARACTER_SPAWN_COUNT }, (_, i) => {
        // const t        = i / (CHARACTER_SPAWN_COUNT - 1);
        const rightOff = (Math.floor(i / 2) - Math.floor(CHARACTER_SPAWN_COUNT / 2 - 1)) * 0.2 + (i % 2) * 0.4;
        const depthOff = 0.3 + (Math.floor(i / 2) % 2) * 0.3;
        return _spawnBase.clone()
            .add(_spawnFwd.clone().multiplyScalar(depthOff))
            .add(_spawnRight.clone().multiplyScalar(rightOff));
    });
};

const SPAWN_POSITIONS = buildSpawnPositions();

export const createCharacterSystem = ({ scene, world, characterInstances, isUnmountedRef }) => {
    const masterClips = [];
    let dracoLoader   = null;

    const playRandomAnim = (inst) => {
        if (!inst.mixer || masterClips.length === 0) return;
        const pool = masterClips.length > 1
            ? masterClips.filter(c => c.name !== inst.currentClipName)
            : masterClips;
        const clip = pool[Math.floor(Math.random() * pool.length)];
        const next = inst.mixer.clipAction(clip);
        next.setLoop(THREE.LoopOnce, 1);
        next.clampWhenFinished = true;
        next.reset();
        if (inst.currentAction) inst.currentAction.crossFadeTo(next, 0.35, true);
        next.play();
        inst.currentAction   = next;
        inst.currentClipName = clip.name;
        inst.lastRootAnimPos.set(0, 0, 0);
    };

    const handleAnimFinished = (inst) => {
        if (!isUnmountedRef.value) playRandomAnim(inst);
    };

    const spawnInstances = (masterModel) => {
        const jointGeo = new THREE.SphereGeometry(1, 6, 6);
        const jointMat = new THREE.MeshBasicMaterial({ color: 0xff4400, wireframe: true });

        for (let i = 0; i < CHARACTER_SPAWN_COUNT; i++) {
            if (isUnmountedRef.value) break;

            const player = SkeletonUtils.clone(masterModel);
            const sp     = SPAWN_POSITIONS[i];

            player.scale.setScalar(0.11 + Math.random() * 0.01);
            player.position.set(sp.x, sp.y + SPAWN_HEIGHT, sp.z);
            player.rotation.z = (i % 2 === 0) * Math.PI;
            player.rotation.x = Math.PI * -0.5;

            player.traverse(child => {
                if (child.isMesh) {
                    child.castShadow    = true;
                    child.receiveShadow = false;
                    if (child.material) {
                        child.material.depthTest          = true;
                        child.material.depthWrite         = true;
                        child.material.roughness          = Math.max(child.material.roughness || 0, 0.4);
                        child.material.emissiveIntensity  = 0;
                        child.material.needsUpdate        = true;
                    }
                }
            });
            scene.add(player);

            const mixer = new THREE.AnimationMixer(player);
            const inst  = {
                model:             player,
                mixer,
                currentAction:     null,
                currentClipName:   null,
                isBeingDragged:    false,
                isAnimationPaused: false,
                groundY:           sp.y,
                rootBone:          findHipsBone(player),
                lastRootAnimPos:   new THREE.Vector3(),
            };
            mixer.addEventListener('finished', () => handleAnimFinished(inst));

            inst.mainBody = createCharacterPhysicsBody(world, { x: sp.x, y: sp.y + SPAWN_HEIGHT, z: sp.z });

            // Ragdoll joint bodies (kinematic, animation-driven)
            const ragdollBodies = [];
            const boneLookup    = {};
            player.traverse(obj => { if (obj.isBone) boneLookup[obj.name] = obj; });

            for (const { bone: boneName, r } of RAGDOLL_JOINTS) {
                const bone = boneLookup[boneName];
                if (!bone) continue;

                const body = new CANNON.Body({ mass: 0, type: CANNON.Body.KINEMATIC });
                body.addShape(new CANNON.Sphere(r));
                world.addBody(body);

                const debugMesh = new THREE.Mesh(jointGeo, jointMat);
                debugMesh.scale.setScalar(r);
                debugMesh.visible     = SHOW_RAGDOLL_DEBUG;
                debugMesh.renderOrder = 2;
                scene.add(debugMesh);

                ragdollBodies.push({ bone, body, debugMesh, boneName });
            }
            inst.ragdollBodies = ragdollBodies;

            characterInstances.push(inst);
            playRandomAnim(inst);
        }

        window.myCharacters = characterInstances;
        console.info(`[Characters] ${CHARACTER_SPAWN_COUNT} 个角色实例已生成`);
    };

    const loadCharacters = () => {
        dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');

        const gltfLoader = new GLTFLoader();
        gltfLoader.setDRACOLoader(dracoLoader);

        gltfLoader.load(CHARACTER_GLB_URL, (gltf) => {
            if (isUnmountedRef.value) return;
            const masterModel = gltf.scene;
            console.info('[Characters] GLB master 已加载，并行加载 FBX 动画...');

            const fbxLoader   = new FBXLoader();
            const fbxPromises = CHARACTER_ANIM_FILES.map(file =>
                new Promise(resolve => {
                    fbxLoader.load(
                        `/animations/${encodeURIComponent(file)}`,
                        (fbx) => {
                            if (isUnmountedRef.value) { resolve(null); return; }
                            const raw = fbx.animations[0];
                            if (!raw) { resolve(null); return; }
                            const clip = remapMixamoClip(raw, masterModel);
                            clip.name  = file.replace('.fbx', '');
                            resolve(clip);
                        },
                        undefined,
                        (err) => { console.warn(`[Characters] FBX 加载失败: ${file}`, err); resolve(null); }
                    );
                })
            );

            Promise.all(fbxPromises).then(clips => {
                if (isUnmountedRef.value) return;
                clips.filter(Boolean).forEach(c => masterClips.push(c));
                console.info(`[Characters] ${masterClips.length} 个 clip 就绪，开始生成实例`);
                spawnInstances(masterModel);
            });
        },
        undefined,
        (err) => console.error('[Characters] GLB 加载失败:', err));
    };

    const dispose = () => {
        for (const inst of characterInstances) {
            if (inst.resumeAnimTimer) clearTimeout(inst.resumeAnimTimer);
            if (inst.mixer) {
                inst.mixer.stopAllAction();
                inst.mixer.uncacheRoot(inst.model);
            }
            if (inst.mainBody) world.removeBody(inst.mainBody);
            if (inst.ragdollBodies) {
                for (const { body, debugMesh } of inst.ragdollBodies) {
                    world.removeBody(body);
                    if (debugMesh) {
                        scene.remove(debugMesh);
                        debugMesh.geometry?.dispose();
                    }
                }
            }
            if (inst.model) {
                scene.remove(inst.model);
                inst.model.traverse(child => {
                    if (child.isMesh) {
                        child.geometry?.dispose();
                        if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
                        else child.material?.dispose();
                    }
                });
            }
        }
        characterInstances.length = 0;
        masterClips.length = 0;
        if (dracoLoader) { dracoLoader.dispose(); dracoLoader = null; }
        delete window.myCharacters;
    };

    return { loadCharacters, dispose };
};
